"use client"

import { useEffect } from "react"
import { logger } from "@/lib/logger"
import { isOfflineFirstEnabled } from "@/lib/feature-flags"
import { errText } from "@/lib/errors"

const log = logger.scope("ServiceWorkerController")

/**
 * Service worker policy controller.
 *
 * Offline-first OFF (default kill path for orphaned Workbox):
 *   Unregister ALL service workers and delete every cache — same as the
 *   historical SwKillSwitch. Tablets with frozen next-pwa workers recover
 *   on next load.
 *
 * Offline-first ON (Phase 1 shell SW — docs/OFFLINE_FIRST_DESIGN.md):
 *   Register `/sw.js` for shell caching + offline navigation fallback.
 *   Does NOT wipe caches on every load (that would defeat the shell).
 *
 * Mounted once in app/layout.tsx.
 */
export function SwKillSwitch() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return

    if (!isOfflineFirstEnabled()) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
        .catch((error: unknown) => {
          log.warn("service worker unregister failed", {
            error: errText(error),
          })
        })
      if ("caches" in window) {
        caches
          .keys()
          .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
          .catch((error: unknown) => {
            log.warn("cache clear failed", {
              error: errText(error),
            })
          })
      }
      return
    }

    // Offline-first enabled: register shell SW
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        log.info("shell service worker registered", {
          scope: reg.scope,
        })
      })
      .catch((error: unknown) => {
        log.warn("shell service worker register failed", {
          error: errText(error),
        })
      })
  }, [])

  return null
}
